import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { API, useCart } from '../App';

export default function FlutterwaveCompletePage() {
  const [params] = useSearchParams();
  const [state, setState] = useState('loading'); // loading | success | failed
  const [amount, setAmount] = useState(null);
  const [currency, setCurrency] = useState('');
  const { clearCart } = useCart();

  useEffect(() => {
    const transaction_id = params.get('transaction_id');
    const tx_ref = params.get('tx_ref');
    const flwStatus = params.get('status');

    if (!transaction_id || flwStatus === 'cancelled') {
      setState('failed');
      return;
    }

    (async () => {
      try {
        const r = await axios.get(`${API}/payments/flutterwave/verify`, {
          params: { transaction_id, tx_ref, status: flwStatus },
        });
        setAmount(r.data.amount);
        setCurrency(r.data.currency || '');
        if (r.data.payment_status === 'successful') {
          setState('success');
          try { await clearCart(); } catch { /* no-op */ }
        } else {
          setState('failed');
        }
      } catch {
        setState('failed');
      }
    })();
  }, [params, clearCart]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="max-w-md w-full text-center" data-testid="flutterwave-complete">
          {state === 'loading' && (
            <>
              <Loader2 className="h-12 w-12 mx-auto animate-spin text-[hsl(var(--gold))] mb-4" />
              <h1 className="text-2xl font-heading font-bold mb-2">Verifying your payment…</h1>
              <p className="text-muted-foreground text-sm">Please don&apos;t close this page.</p>
            </>
          )}
          {state === 'success' && (
            <>
              <CheckCircle2 className="h-14 w-14 mx-auto text-green-600 mb-4" />
              <h1 className="text-3xl font-heading font-bold text-[hsl(var(--navy))] mb-2" data-testid="flw-success-title">
                Payment successful
              </h1>
              <p className="text-muted-foreground mb-6">
                {amount && currency ? `We received your ${currency} ${amount}. ` : ''}
                Your order is confirmed and will be fulfilled by the vendor shortly.
                A receipt is being emailed to you.
              </p>
              <div className="flex gap-3 justify-center">
                <Link to="/orders"><Button data-testid="flw-view-orders">View my orders</Button></Link>
                <Link to="/products"><Button variant="outline">Keep shopping</Button></Link>
              </div>
            </>
          )}
          {state === 'failed' && (
            <>
              <XCircle className="h-14 w-14 mx-auto text-red-600 mb-4" />
              <h1 className="text-3xl font-heading font-bold text-[hsl(var(--navy))] mb-2" data-testid="flw-failed-title">
                Payment not completed
              </h1>
              <p className="text-muted-foreground mb-6">
                We could not confirm your payment. Your cart is saved — you can try again with
                Stripe or a different Flutterwave method.
              </p>
              <div className="flex gap-3 justify-center">
                <Link to="/cart"><Button data-testid="flw-back-to-cart">Back to cart</Button></Link>
                <Link to="/products"><Button variant="outline">Keep shopping</Button></Link>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
